// ABOUTME: Pure helpers for client-side pennant team statistics
// ABOUTME: Keeps HUD stats deterministic for loaded, simulated, and local pennants

// Extract the authoritative pennant total from a GET /stats payload
// ({ pennant_count }). Returns null for missing/invalid data so callers can
// fall back to the local optimistic count (e.g. the DB-less demo has no API).
export function parseStatsCount(payload) {
    const raw = payload?.pennant_count;
    if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return null;
    return Math.floor(raw);
}

// Pick the number the counter should show.
//
// When online, the authoritative DB total from /stats wins outright, plus a
// transient optimistic bump for the user's OWN plants that the last /stats poll
// hasn't reflected yet (pendingLocalPlants) so the counter ticks up instantly.
// The bump is cleared on the next poll (stats then includes those plants), so
// the display cannot drift upward — unlike the old max(stats, local), where a
// stale/over-counted local value would inflate the counter and never come down.
//
// When offline (no /stats, e.g. the DB-less demo), fall back to the local count.
//
// The second argument may be an options object { localFallback, pendingLocalPlants }
// or a bare number (treated as localFallback with no pending plants).
export function resolveDisplayedCount(statsCount, options = {}) {
    const opts = typeof options === 'number' ? { localFallback: options } : options;
    const local = Number.isFinite(opts.localFallback) ? opts.localFallback : 0;
    const pending = Number.isFinite(opts.pendingLocalPlants) ? Math.max(0, opts.pendingLocalPlants) : 0;
    if (statsCount === null || statsCount === undefined) return local;
    return statsCount + pending;
}

export function createTeamCounts(teams) {
    return new Map(teams.map((team) => [team.code, 0]));
}

export function incrementTeamCount(counts, teamCode, amount = 1) {
    if (!teamCode) return counts;
    counts.set(teamCode, (counts.get(teamCode) ?? 0) + amount);
    return counts;
}

export function summarizeTeamCounts(counts, teams, { limit = Infinity } = {}) {
    const teamByCode = new Map(teams.map((team) => [team.code, team]));
    const rows = [];
    let total = 0;

    for (const [code, count] of counts) {
        if (count <= 0) continue;
        total += count;
        const team = teamByCode.get(code);
        rows.push({
            code,
            count,
            nameDe: team?.nameDe ?? code,
            nameEn: team?.nameEn ?? code,
            emblem: team?.emblem ?? null,
            primary: team?.primary ?? '#FFFFFF',
        });
    }

    rows.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.nameDe.localeCompare(b.nameDe, 'de');
    });

    const leader = rows[0] ?? null;
    const leaderShare = leader && total > 0 ? leader.count / total : 0;

    return {
        total,
        representedTeamCount: rows.length,
        leader,
        leaderShare,
        rows,
        topRows: rows.slice(0, limit),
    };
}
